import {
  useController,
  type ControllerRenderProps,
  type FieldValues,
} from "react-hook-form";

export type FieldValidator = (value: unknown) => string | undefined;

export interface UseControlledFieldOptions<TValue = unknown> {
  name: string;
  validator?: FieldValidator;
  defaultValue?: TValue;
}

/**
 * Adapter around RHF's useController that accepts the project's existing
 * Kendo-style `validator` signature `(value) => "" | "msg"` and translates it
 * to RHF's `rules.validate`.
 *
 * Use inside RHF-aware Forms components so consumers can keep their existing
 * `validator={requiredValidator}` patterns unchanged after the codemod.
 *
 * Requires a surrounding `FormProvider`; `useController` will throw if one is
 * missing, matching the rest of the RHF surface (e.g. `useFormApiCtx`).
 */
export function useControlledField<TValue = unknown>({
  name,
  validator,
}: UseControlledFieldOptions<TValue>): {
  field: ControllerRenderProps<FieldValues, string>;
  fieldState: ReturnType<typeof useController>["fieldState"];
} {
  return useController({
    name,
    rules: validator
      ? {
          validate: (v: unknown) => {
            const msg = validator(v);
            return typeof msg === "string" && msg.length > 0 ? msg : true;
          },
        }
      : undefined,
  });
}
